import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatMoney } from './analyticsHelpers';

// =============================================================================
// FINANCIAL PROTOCOL — READ BEFORE EDITING THIS FILE
// -----------------------------------------------------------------------------
// This card visualises the paid-transaction mix grouped by host commission
// tier (base 0.5% / unlocked 2% / future 3%). Numbers come straight from the
// backend's tier-mix aggregation — never hand-tweak constants here. Source of
// truth: /app/memory/FINANCIAL_PROTOCOL.md + /app/backend/utils.py.
// =============================================================================

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGE_OPTIONS = [
  { id: '7', days: 7, label: '7d' },
  { id: '30', days: 30, label: '30d' },
  { id: '90', days: 90, label: '90d' },
];

const TIER_COLORS = {
  base: { fill: '#0F6B19', bg: '#EAF7EE', border: '#BCE6C8', text: '#0F6B19' },
  unlocked: { fill: '#703FF6', bg: '#F3EEFF', border: '#D9C9FF', text: '#3F1F8C' },
  future: { fill: '#A36500', bg: '#FFF7EE', border: '#F3D9B0', text: '#7A4C0E' },
};

const cardStyle = {
  background: '#FFFFFF',
  borderRadius: 18,
  padding: 16,
  border: '1px solid rgba(112, 63, 246, 0.08)',
  boxShadow: 'var(--premium-shadow-nested)',
};

const TierColumn = ({ tier, maxTx, maxRevenue }) => {
  const palette = TIER_COLORS[tier.id] || TIER_COLORS.base;
  const txShare = maxTx ? (tier.transactions / maxTx) * 100 : 0;
  const revShare = maxRevenue ? (tier.allure_net / maxRevenue) * 100 : 0;
  const isFuture = tier.id === 'future';

  return (
    <div
      data-testid={`tier-mix-column-${tier.id}`}
      style={{
        flex: 1,
        minWidth: 200,
        display: 'grid',
        gap: 10,
        padding: 14,
        borderRadius: 14,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        opacity: isFuture && tier.transactions === 0 ? 0.7 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: palette.text, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {tier.label}
          </p>
          <p
            data-testid={`tier-mix-${tier.id}-allure-net`}
            style={{ margin: '4px 0 0', fontSize: 24, fontWeight: 700, color: '#111' }}
          >
            {formatMoney(tier.allure_net)}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 11, color: '#666' }}>Allure net keep</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p
            data-testid={`tier-mix-${tier.id}-allure-net-pct`}
            style={{ margin: 0, fontSize: 16, fontWeight: 700, color: palette.text }}
          >
            {tier.allure_net_pct.toFixed(1)}%
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 10, color: '#888' }}>of revenue</p>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 6 }}>
        {/* Transactions bar */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#444', marginBottom: 3 }}>
            <span>Transactions</span>
            <span data-testid={`tier-mix-${tier.id}-transactions`}>
              <strong>{tier.transactions}</strong> · {tier.transactions_pct.toFixed(1)}%
            </span>
          </div>
          <div style={{ height: 8, background: '#FFF', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.05)' }}>
            <div
              style={{
                width: `${Math.max(0, Math.min(100, txShare))}%`,
                height: '100%',
                background: palette.fill,
                borderRadius: 6,
                transition: 'width 240ms ease',
              }}
            />
          </div>
        </div>

        {/* Revenue bar */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#444', marginBottom: 3 }}>
            <span>Allure revenue</span>
            <span><strong>{formatMoney(tier.allure_net)}</strong></span>
          </div>
          <div style={{ height: 8, background: '#FFF', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.05)' }}>
            <div
              style={{
                width: `${Math.max(0, Math.min(100, revShare))}%`,
                height: '100%',
                background: palette.fill,
                opacity: 0.55,
                borderRadius: 6,
                transition: 'width 240ms ease',
              }}
            />
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 2, fontSize: 11, color: '#555', paddingTop: 6, borderTop: '1px dashed rgba(0,0,0,0.08)' }}>
        <p style={{ margin: 0 }}>
          Volume: <strong data-testid={`tier-mix-${tier.id}-volume`}>{formatMoney(tier.volume)}</strong>
          <span style={{ color: '#999' }}> ({tier.volume_pct.toFixed(1)}%)</span>
        </p>
        <p style={{ margin: 0 }}>Host commission: <strong>{formatMoney(tier.host_commission)}</strong></p>
        <p style={{ margin: 0 }}>Pool contribution: <strong>{formatMoney(tier.pool)}</strong></p>
      </div>

      {isFuture ? (
        <p style={{ margin: 0, fontSize: 10, color: '#A36500', fontStyle: 'italic' }}>
          Not yet wired into payouts — unlocked once Allure reaches network scale.
        </p>
      ) : null}
    </div>
  );
};

export const TierMixCard = ({ headers }) => {
  const [rangeId, setRangeId] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const days = useMemo(
    () => (RANGE_OPTIONS.find((opt) => opt.id === rangeId) || RANGE_OPTIONS[1]).days,
    [rangeId],
  );

  const fetchTierMix = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics/tier-mix?days=${days}`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load tier mix');
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('tier mix load failed', err);
      }
    } finally {
      setLoading(false);
    }
  }, [days, headers]);

  useEffect(() => {
    fetchTierMix();
  }, [fetchTierMix]);

  const tiers = data?.tiers || [];
  const totals = data?.totals || { transactions: 0, volume: 0, allure_net: 0 };

  const maxTx = useMemo(() => Math.max(1, ...tiers.map((t) => t.transactions)), [tiers]);
  const maxRevenue = useMemo(() => Math.max(0.01, ...tiers.map((t) => t.allure_net)), [tiers]);

  return (
    <div style={cardStyle} data-testid="admin-finance-tier-mix-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <p style={{ margin: 0, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Where the revenue comes from
          </p>
          <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, margin: '4px 0 0', color: '#000' }}>
            Tier mix · last {days} days
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#888' }}>
            How much traffic + clean profit comes from each host commission tier.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6 }} role="tablist" aria-label="Range">
          {RANGE_OPTIONS.map((opt) => {
            const selected = rangeId === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                role="tab"
                aria-selected={selected}
                data-testid={`tier-mix-range-${opt.id}`}
                onClick={() => setRangeId(opt.id)}
                style={{
                  border: selected ? '2px solid #111' : '1px solid #E0E0E0',
                  background: selected ? '#111' : '#FFF',
                  color: selected ? '#FFF' : '#444',
                  borderRadius: 999,
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading && !data ? (
        <p data-testid="tier-mix-loading" style={{ textAlign: 'center', color: '#999', padding: 30 }}>Loading tier mix…</p>
      ) : totals.transactions === 0 ? (
        <p data-testid="tier-mix-empty" style={{ textAlign: 'center', color: '#999', padding: 30 }}>
          No paid transactions in this window yet.
        </p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {tiers.map((tier) => (
              <TierColumn key={tier.id} tier={tier} maxTx={maxTx} maxRevenue={maxRevenue} />
            ))}
          </div>

          <div
            data-testid="tier-mix-totals"
            style={{
              marginTop: 12,
              padding: '10px 14px',
              borderRadius: 12,
              background: '#F7F4FE',
              border: '1px dashed #D9C9FF',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 12,
              color: '#3F1F8C',
            }}
          >
            <span>
              Totals — <strong>{totals.transactions}</strong> transactions
              {' · '}<strong>{formatMoney(totals.volume)}</strong> volume
              {' · '}<strong>{formatMoney(totals.allure_net)}</strong> Allure net
            </span>
            <span style={{ color: '#888' }}>
              Refresh window: {new Date(data.start).toLocaleDateString('en-GB', { timeZone: 'Europe/Athens' })} → {new Date(data.end).toLocaleDateString('en-GB', { timeZone: 'Europe/Athens' })}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default TierMixCard;
